import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

/**
 * Background hook that watches the device GPS and periodically
 * POSTs the position to the backend.  Does nothing unless the
 * user has opted in via `user.location_sharing === true`.
 *
 * Throttle: at most one POST every 20 seconds.
 */
export default function useLocationSharing() {
  const { user } = useAuth();
  const lastPost = useRef(0);

  useEffect(() => {
    if (!user?.location_sharing) return;
    if (!navigator.geolocation) return;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastPost.current < 20_000) return; // throttle
        lastPost.current = now;
        api
          .post("/users/me/location", {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          })
          .catch(() => {}); // swallow — best-effort
      },
      () => {}, // silently ignore errors
      { enableHighAccuracy: true, maximumAge: 15_000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [user?.location_sharing]);
}
